/**
 * Unified CEU Configuration Manager
 * Handles both RID and BEI certification requirements
 */

import { 
  BEI_CERTIFICATE_TYPES, 
  BEI_CATEGORIES, 
  BEI_RULES,
  getBEIRequirements,
  calculateBEIProgress,
  validateBEICEU
} from './beiConfig';

import {
  RID_CERTIFICATE_TYPES,
  RID_CATEGORIES,
  RID_RULES,
  getRIDRequirements,
  calculateRIDProgress,
  validateRIDCEU
} from './ridConfig';

/**
 * Determine if a certifying body is BEI
 * @param {string} certifyingBody - The certifying body
 * @returns {boolean} True if BEI
 */
export function isBEI(certifyingBody) {
  return certifyingBody === 'BEI';
}

/**
 * Determine if a certifying body is RID
 * @param {string} certifyingBody - The certifying body
 * @returns {boolean} True if RID
 */
export function isRID(certifyingBody) {
  return certifyingBody === 'RID';
}

/**
 * Get all available certificate types for a certifying body
 * @param {string} certifyingBody - 'RID' or 'BEI'
 * @returns {Object} Certificate types object
 */
export function getCertificateTypes(certifyingBody) {
  if (isBEI(certifyingBody)) {
    return BEI_CERTIFICATE_TYPES;
  } else if (isRID(certifyingBody)) {
    return RID_CERTIFICATE_TYPES;
  }
  return {};
}

/**
 * Get available CEU categories for a certifying body
 * @param {string} certifyingBody - 'RID' or 'BEI'
 * @returns {Object} Categories object
 */
export function getCEUCategories(certifyingBody) {
  if (isBEI(certifyingBody)) {
    return BEI_CATEGORIES;
  } else if (isRID(certifyingBody)) {
    return RID_CATEGORIES;
  }
  return {};
}

/**
 * Get requirements for a specific certificate
 * @param {string} certifyingBody - 'RID' or 'BEI'
 * @param {string} certificateType - Certificate type
 * @returns {Object} Requirements object
 */
export function getRequirements(certifyingBody, certificateType) {
  if (isBEI(certifyingBody)) {
    return getBEIRequirements(certificateType);
  } else if (isRID(certifyingBody)) {
    return getRIDRequirements(certificateType);
  }
  
  // Default fallback
  return {
    totalRequired: 8.0,
    cycleYears: 4,
    requirements: {
      'Professional Studies': { min: 6.0, max: 8.0 },
      'General Studies': { min: 0.0, max: 2.0 }
    }
  };
}

/**
 * Calculate CEU progress for a user
 * @param {Array} ceus - Array of CEU records
 * @param {string} certifyingBody - 'RID' or 'BEI'
 * @param {string} certificateType - Certificate type
 * @returns {Object} Progress calculation
 */
export function calculateProgress(ceus, certifyingBody, certificateType) {
  if (isBEI(certifyingBody)) {
    return calculateBEIProgress(ceus, certificateType);
  } else if (isRID(certifyingBody)) {
    return calculateRIDProgress(ceus, certificateType);
  }
  
  // Default fallback calculation
  const total = ceus.reduce((sum, ceu) => sum + parseFloat(ceu.ceuHours || 0), 0);
  return {
    total,
    isComplete: total >= 8.0,
    totalRequired: 8.0,
    cycleYears: 4,
    warnings: total < 8.0 ? [`Need ${(8.0 - total).toFixed(1)} more CEUs`] : []
  };
}

/**
 * Validate a CEU record
 * @param {Object} ceu - CEU record
 * @param {Array} existingCeus - Existing CEU records
 * @param {string} certifyingBody - 'RID' or 'BEI'
 * @param {string} certificateType - Certificate type
 * @returns {Object} Validation result
 */
export function validateCEU(ceu, existingCeus, certifyingBody, certificateType) {
  if (isBEI(certifyingBody)) {
    return validateBEICEU(ceu, existingCeus, certificateType);
  } else if (isRID(certifyingBody)) {
    return validateRIDCEU(ceu, existingCeus, certificateType);
  }
  
  // Default validation
  return {
    isValid: true,
    errors: [],
    warnings: []
  };
}

/**
 * Get rules and information for a certifying body
 * @param {string} certifyingBody - 'RID' or 'BEI'
 * @returns {Object} Rules object
 */
export function getRules(certifyingBody) {
  if (isBEI(certifyingBody)) {
    return BEI_RULES;
  } else if (isRID(certifyingBody)) {
    return RID_RULES;
  }
  return {};
}

/**
 * Get formatted certificate options for dropdowns
 * @param {string} certifyingBody - 'RID' or 'BEI'
 * @returns {Array} Array of {value, label, description} objects
 */
export function getCertificateOptions(certifyingBody) {
  const types = getCertificateTypes(certifyingBody);
  return Object.entries(types).map(([key, config]) => ({
    value: key,
    label: config.name,
    description: config.description
  }));
}

/**
 * Get formatted category options for dropdowns
 * @param {string} certifyingBody - 'RID' or 'BEI'
 * @returns {Array} Array of {value, label, description} objects
 */
export function getCategoryOptions(certifyingBody) {
  const categories = getCEUCategories(certifyingBody);
  return Object.entries(categories).map(([key, config]) => ({
    value: key,
    label: config.label,
    description: config.description
  }));
}

/**
 * Get subcategory options for a specific category
 * @param {string} certifyingBody - 'RID' or 'BEI'
 * @param {string} category - Category name
 * @returns {Array} Array of subcategory objects
 */
export function getSubcategoryOptions(certifyingBody, category) {
  const categories = getCEUCategories(certifyingBody);
  return categories[category]?.subcategories || [];
}

/**
 * Get cycle information for display
 * @param {string} certifyingBody - 'RID' or 'BEI'
 * @param {string} certificateType - Certificate type
 * @returns {Object} Cycle information
 */
export function getCycleInfo(certifyingBody, certificateType) {
  const requirements = getRequirements(certifyingBody, certificateType);
  
  return {
    years: requirements.cycleYears,
    totalRequired: requirements.totalRequired,
    certifyingBody,
    certificateType,
    description: `${requirements.totalRequired} CEUs every ${requirements.cycleYears} years`
  };
}

/**
 * Check if a CEU category is valid for the certifying body
 * @param {string} category - Category to check
 * @param {string} certifyingBody - 'RID' or 'BEI'
 * @returns {boolean} True if valid
 */
export function isValidCategory(category, certifyingBody) {
  const categories = getCEUCategories(certifyingBody);
  return categories.hasOwnProperty(category);
}

/**
 * Convert between RID and BEI categories when switching certifying bodies
 * @param {string} fromCategory - Original category
 * @param {string} fromBody - Original certifying body
 * @param {string} toBody - Target certifying body
 * @returns {string} Converted category or closest match
 */
export function convertCategory(fromCategory, fromBody, toBody) {
  // If same body, no conversion needed
  if (fromBody === toBody) return fromCategory;
  
  // RID to BEI conversions
  if (isRID(fromBody) && isBEI(toBody)) {
    const conversions = {
      'Professional Studies': 'Interpreting',
      'General Studies': 'General'
    };
    return conversions[fromCategory] || 'Interpreting';
  }
  
  // BEI to RID conversions
  if (isBEI(fromBody) && isRID(toBody)) {
    const conversions = {
      'Interpreting': 'Professional Studies',
      'Ethics': 'Professional Studies',
      'Court': 'Professional Studies',
      'Medical': 'Professional Studies',
      'Trilingual': 'Professional Studies',
      'General': 'General Studies'
    };
    return conversions[fromCategory] || 'Professional Studies';
  }
  
  return fromCategory;
}

/**
 * Get progress summary text for display
 * @param {Object} progress - Progress object from calculateProgress
 * @returns {string} Summary text
 */
export function getProgressSummary(progress) {
  const { total, totalRequired, isComplete } = progress;
  
  if (isComplete) {
    return `✅ Complete: ${total.toFixed(1)}/${totalRequired} CEUs`;
  } else {
    const remaining = totalRequired - total;
    return `📋 In Progress: ${total.toFixed(1)}/${totalRequired} CEUs (${remaining.toFixed(1)} remaining)`;
  }
}

// Export all configurations for direct access if needed
export {
  BEI_CERTIFICATE_TYPES,
  BEI_CATEGORIES,
  BEI_RULES,
  RID_CERTIFICATE_TYPES,
  RID_CATEGORIES,
  RID_RULES
};

const ceuConfig = {
  isBEI,
  isRID,
  getCertificateTypes,
  getCEUCategories,
  getRequirements,
  calculateProgress,
  validateCEU,
  getRules,
  getCertificateOptions,
  getCategoryOptions,
  getSubcategoryOptions,
  getCycleInfo,
  isValidCategory,
  convertCategory,
  getProgressSummary
};

export default ceuConfig;
